import React, { useState, useEffect } from 'react';
import AppShell from './components/AppShell';
import { arrangeNavItems, isPageAvailable } from './config/pages';
import Dashboard from './components/Dashboard';
import Fields from './components/Fields';
import Weather from './components/Weather';
import Tasks from './components/Tasks';
import TaskForm from './components/TaskForm';
import Inventory from './components/Inventory';
import WorkerInterface from './components/WorkerInterface';
import Settings from './components/Settings';
import ParcelManager from './components/ParcelManager';
import SeasonManager from './components/SeasonManager';
import StockAdjustment from './components/StockAdjustment';
import WorkPlanning from './components/WorkPlanning';
import ArendaManager from './components/ArendaManager';
import EquipmentManager from './components/EquipmentManager';
import Motorina from './components/Motorina';
import Lucrari from './components/Lucrari';
import Costuri from './components/Costuri';

import universalDataService from './services/universalDataService';
import cloudSyncService from './services/cloudSyncService';
import accessControlService from './services/accessControlService';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import FirebaseFarmLogin from './components/FirebaseFarmLogin';
import FirebaseFarmRegistration from './components/FirebaseFarmRegistration';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import firebaseAuthService from './services/firebaseAuthService';
import { getCurrentUser, logout, hasRole } from './services/authService';
import { useTranslation } from './services/translationService';
import './App.css';

// Fetches the latest farm data. A device that already has the data shows it straight away and
// updates in the background. Only a first sign-in waits, and never longer than FIRST_SYNC_LIMIT_MS.
const FIRST_SYNC_LIMIT_MS = 15000;
const deviceHasFarmData = (farmId) => Boolean(
  localStorage.getItem(`fieldsData_${farmId}`) || localStorage.getItem(`parcelsData_${farmId}`)
);
const syncOnStart = (farmId, silent) => {
  const download = cloudSyncService.downloadAllDataOnLogin(farmId, silent).catch((error) => {
    console.warn('Sync on start failed (offline?):', error);
    return false;
  });
  if (deviceHasFarmData(farmId)) return Promise.resolve(true);
  return Promise.race([
    download,
    new Promise((resolve) => setTimeout(() => resolve(false), FIRST_SYNC_LIMIT_MS)),
  ]);
};

const LoadingScreen = () => (
  <div role="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', fontFamily: "'IBM Plex Sans', sans-serif" }}>
    <div style={{ textAlign: 'center' }}>
      <img src="/logo-farmtrackpro.svg" alt="" style={{ height: '60px', marginBottom: '16px', borderRadius: '14px' }} />
      <div style={{ width: '36px', height: '36px', border: '4px solid #E2E6E0', borderTop: '4px solid #14432F', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
      <p style={{ margin: '14px 0 0', color: '#56635B', fontSize: '15px' }}>Se încarcă datele…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

function App() {
  const { t } = useTranslation();
  // State for new multi-tenant authentication
  const [farmAuth, setFarmAuth] = useState({ 
    isAuthenticated: false, 
    user: null, 
    farm: null 
  });
  
  // State for legacy authentication (keeping for backward compatibility)
  const [auth, setAuth] = useState({ isAuthenticated: false, user: null });
  
  // State for authentication mode
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'superadmin'
  
  // True while Firebase resolves the session on first load — prevents login flash
  const [authLoading, setAuthLoading] = useState(true);
  const [dataDownloading, setDataDownloading] = useState(false);

  // State for active component/page
  const [activeComponent, setActiveComponent] = useState('fields');
  
  // State for storing data - now farm-specific
  const [data, setData] = useState({
    fields: [],
    tasks: [],
    weather: { current: {}, forecast: [] },
    financialMetrics: { revenue: {}, expenses: {}, profit: {} },
    inventory: { materials: [], equipments: [], invoices: [] },
    workplans: [],
    workers: []
  });
  
  // Selected task for editing
  const [selectedTask, setSelectedTask] = useState(null);
  
  // State for showing/hiding the task form
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [navigationKey, setNavigationKey] = useState(0);

  // Check if user is already logged in on component mount
  useEffect(() => {
    // Check for Firebase authentication
    const checkAuth = async () => {
      try {
        const currentUser = await firebaseAuthService.getCurrentUser();
        if (currentUser) {
          setFarmAuth({
            isAuthenticated: true,
            user: currentUser.user,
            farm: currentUser.farm
          });

          // Download all Firebase data on every page load / refresh so that
          // any device (mobile, tablet, another browser) always starts with
          // the latest data without requiring an explicit logout + login.
          try {
            localStorage.setItem('currentFarmId', currentUser.farm.id);
            cloudSyncService.setupInstantRefreshSync(currentUser.farm.id);
            await syncOnStart(currentUser.farm.id, true /* silent */);
          } catch (syncErr) {
            console.warn('⚠️ Page-load sync failed (offline?):', syncErr);
          }

          // Set initial active component based on user role
          if (currentUser.user.role === 'worker') {
            setActiveComponent('worker');
          } else if (currentUser.user.role === 'accountant') {
            setActiveComponent('parcels');
          } else if (currentUser.user.role === 'tank_operator') {
            setActiveComponent('motorina');
          } else {
            setActiveComponent('fields');
          }
        } else {
          // Fallback to legacy authentication
          const currentAuth = getCurrentUser();
          if (currentAuth.isAuthenticated) {
            setAuth(currentAuth);
            
            // DISABLED AUTO-SYNC - Focus on Force Download only
            console.log('🔄 Auto-sync disabled for legacy users - using Force Download only');
            // cloudSyncService.startRealTimeSync();
            
            // Set initial active component based on user role
            if (currentAuth.user.role === 'worker') {
              setActiveComponent('worker');
            } else if (currentAuth.user.role === 'accountant') {
              setActiveComponent('parcels');
            } else {
              setActiveComponent('fields');
            }
          }
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        // Fallback to legacy authentication
        const currentAuth = getCurrentUser();
        if (currentAuth.isAuthenticated) {
          setAuth(currentAuth);
          
          // Set initial active component based on user role
          if (currentAuth.user.role === 'worker') {
            setActiveComponent('worker');
          } else if (currentAuth.user.role === 'accountant') {
            setActiveComponent('parcels');
          } else {
            setActiveComponent('fields');
          }
        }
      }
    };
    
    checkAuth().finally(() => setAuthLoading(false));
  }, []);

  // Load user permissions when farm user changes
  useEffect(() => {
    if (farmAuth.isAuthenticated && farmAuth.user?.uid) {
      loadUserPermissions(farmAuth.user.uid);
    } else {
      setUserPermissions(null);
    }
  }, [farmAuth.isAuthenticated, farmAuth.user?.uid]);

  // Force navigation re-render when permissions change
  useEffect(() => {
    if (userPermissions !== null) {
      setNavigationKey(prev => prev + 1);
    }
  }, [userPermissions]);

  // Show loading screen while permissions are loading

  // Handle farm login success
  const handleFarmLoginSuccess = async ({ user, farm }) => {
    // A first sign-in on a device starts empty until its data has downloaded.
    setDataDownloading(!deviceHasFarmData(farm.id));
    setFarmAuth({
      isAuthenticated: true,
      user,
      farm
    });
    
    // Initialize cloud sync for the farm
    try {
      localStorage.setItem('currentFarmId', farm.id);
      console.log('☁️ Cloud sync initialized for farm:', farm.name);
      
      // AUTO-DOWNLOAD ALL DATA FROM FIREBASE ON LOGIN using new cloudSyncService
      console.log('🚀 Starting LOGIN SYNC - downloading all data from Firebase...');
      cloudSyncService.setupInstantRefreshSync(farm.id);
      const loginSyncSuccess = await syncOnStart(farm.id, false);

      if (loginSyncSuccess) {
        console.log('✅ LOGIN SYNC COMPLETE - all data downloaded from Firebase');
      } else {
        console.log('⚠️ LOGIN SYNC had issues, but continuing...');
      }
      
    } catch (error) {
      console.error('Error during login sync:', error);
    } finally {
      setDataDownloading(false);
    }

    // Set initial page based on user role
    if (user.role === 'worker') {
      setActiveComponent('worker');
    } else if (user.role === 'accountant') {
      setActiveComponent('parcels');
    } else {
      setActiveComponent('fields');
    }
  };

  // Handle farm registration success
  const handleFarmRegistrationSuccess = ({ user, farm }) => {
    setFarmAuth({
      isAuthenticated: true,
      user,
      farm
    });
    
    // Initialize cloud sync for the new farm
    try {
      localStorage.setItem('currentFarmId', farm.id);
      console.log('☁️ Cloud sync initialized for new farm:', farm.name);
      
      // AUTO-DOWNLOAD ALL DATA FROM FIREBASE ON REGISTRATION
      setTimeout(async () => {
        console.log('🔄 AUTO-DOWNLOADING ALL DATA FROM FIREBASE (NEW FARM)...');
        try {
          // Import Firebase directly
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('./config/firebase');
          
          const docRef = doc(db, 'syncData', farm.id);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const firebaseData = docSnap.data();
            const cloudData = firebaseData.farmData || {};
            
            console.log('📥 AUTO-DOWNLOADING FROM FIREBASE (NEW FARM):', Object.keys(cloudData).length, 'data types');
            
            let downloadCount = 0;
            const currentTimestamp = new Date().toISOString();
            
            // Download each data type from Firebase to localStorage
            for (const [key, dataInfo] of Object.entries(cloudData)) {
              const firebaseActualData = dataInfo.actualData;
              
              // Save in universalDataService format
              const restoredData = {
                data: firebaseActualData,
                lastModified: currentTimestamp,
                synced: true,
                farmId: farm.id,
                dataType: dataInfo.dataType || key.split('_')[0]
              };
              
              localStorage.setItem(key, JSON.stringify(restoredData));
              downloadCount++;
              
              console.log(`📥 AUTO-DOWNLOADED ${key}:`, Array.isArray(firebaseActualData) ? `${firebaseActualData.length} items` : 'object');
            }
            
            // Trigger UI refresh
            const refreshEvents = [
              'parcelDataUpdated',
              'inventoryDataUpdated', 
              'stockAdjustmentDataUpdated',
              'seasonDataUpdated',
              'companySettingsUpdated',
              'tasksDataUpdated',
              'fieldsDataUpdated',
              'weatherDataUpdated',
              'financesDataUpdated',
              'workPlansDataUpdated',
              'settingsDataUpdated',
              'cloudSyncComplete'
            ];
            
            refreshEvents.forEach(eventName => {
              window.dispatchEvent(new CustomEvent(eventName, { detail: 'AUTO-DOWNLOAD COMPLETE (NEW FARM)' }));
            });
            
            console.log(`✅ AUTO-DOWNLOAD COMPLETE (NEW FARM): ${downloadCount} data types downloaded from Firebase`);
          } else {
            console.log('📭 No data found on Firebase for this new farm');
          }
        } catch (error) {
          console.error('❌ Auto-download failed for new farm:', error);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to initialize cloud sync:', error);
    }
    
    setActiveComponent('fields');
  };

  // Handle legacy login success (keeping for backward compatibility)
  const handleLoginSuccess = (authData) => {
    setAuth(authData);
    
    // Set initial page based on user role
    if (authData.user.role === 'worker') {
      setActiveComponent('worker');
    } else if (authData.user.role === 'accountant') {
      setActiveComponent('parcels');
    } else {
      setActiveComponent('fields');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (farmAuth.isAuthenticated) {
      await firebaseAuthService.logout();
      setFarmAuth({ isAuthenticated: false, user: null, farm: null });
    } else {
      logout();
      setAuth({ isAuthenticated: false, user: null });
    }
    
    // Clean up cloud sync
    try {
      localStorage.removeItem('currentFarmId');
      console.log('☁️ Cloud sync cleaned up');
    } catch (error) {
      console.error('Failed to cleanup cloud sync:', error);
    }
  };

  // Handle navigation from sidebar
  const handleNavigation = (component) => {
    setActiveComponent(component);
    
    if (component !== 'taskForm') {
      setShowTaskForm(false);
      setSelectedTask(null);
    }
  };

  // Handle task editing
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowTaskForm(true);
    setActiveComponent('taskForm');
  };

  // Handle task deletion
  const handleDeleteTask = (taskId) => {
    setData(prevData => ({
      ...prevData,
      tasks: prevData.tasks.filter(task => task.id !== taskId)
    }));
  };

  // Handle task addition
  const handleAddTask = () => {
    setSelectedTask(null);
    setShowTaskForm(true);
    setActiveComponent('taskForm');
  };

  // Handle task save
  const handleSaveTask = (task) => {
    setData(prevData => {
      const existingTaskIndex = prevData.tasks.findIndex(t => t.id === task.id);
      
      if (existingTaskIndex >= 0) {
        // Update existing task
        const updatedTasks = [...prevData.tasks];
        updatedTasks[existingTaskIndex] = task;
        return { ...prevData, tasks: updatedTasks };
      } else {
        // Add new task with generated ID
        const newTask = {
          ...task,
          id: `task-${Date.now()}`,
          createdAt: new Date().toISOString()
        };
        return { ...prevData, tasks: [...prevData.tasks, newTask] };
      }
    });
    
    setShowTaskForm(false);
    setActiveComponent('tasks');
  };

  // Handle task completion
  const handleCompleteTask = (taskId) => {
    setData(prevData => {
      const updatedTasks = prevData.tasks.map(task => 
        task.id === taskId 
          ? { ...task, status: 'completed', updatedAt: new Date().toISOString() } 
          : task
      );
      
      return { ...prevData, tasks: updatedTasks };
    });
  };

  // Check permissions for new farm auth system
  const hasFarmPermission = (permission) => {
    if (!farmAuth.isAuthenticated) return false;
    if (farmAuth.user.role === 'manager') return true;
    return farmAuth.user.permissions.includes(permission);
  };

  // Load user permissions
  const loadUserPermissions = async (userId) => {
    if (!userId) {
      console.log('❌ No userId provided to loadUserPermissions');
      return;
    }
    
    console.log('🔄 Loading permissions for user:', userId);
    
    try {
      setPermissionsLoading(true);
      const permissions = await accessControlService.getUserPermissions(userId);
      console.log('✅ Loaded permissions:', permissions);
      setUserPermissions(permissions);
    } catch (error) {
      console.error('❌ Error loading user permissions:', error);
      setUserPermissions(null);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Show V2 components if V2 UI is enabled
  const renderContent = () => {
    if (authLoading || dataDownloading) return <LoadingScreen />;

    if (!farmAuth.isAuthenticated) {
      return <FirebaseFarmLogin />;
    }

    // A hidden page (see config/pages.js) opens the user's first available page instead
    const page = resolvePage();

    // Check if user has permission to access the current page
    const user = farmAuth.user;
    if (user && user.role !== 'manager' && userPermissions) {
      const allowedPages = userPermissions.pages || [];
      const currentPagePermission = page;
      
      // Map some component names to permission names
      const permissionMap = {
        'dashboard': 'dashboard',
        'fields': 'fields',
        'weather': 'weather',
        'tasks': 'tasks',
        'inventory': 'inventory',
        'worker': 'worker',
        'finances': 'finances',
        'parcels': 'parcels',
        'seasons': 'seasons',
        'stockadjustment': 'stockadjustment',
        'workplanning': 'workplanning',
        'arendamanager': 'arendamanager',
        'settings': 'settings'
      };
      
      const requiredPermission = permissionMap[currentPagePermission];
      
      if (requiredPermission && 
          !allowedPages.includes('all') && 
          !allowedPages.includes(requiredPermission)) {
        // User doesn't have permission, redirect to first allowed page or show access denied
        const allowedNavItems = getAvailableNavItems();
        if (allowedNavItems.length > 0) {
          // Redirect to first allowed page
          setTimeout(() => setActiveComponent(allowedNavItems[0].id), 100);
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h3>🚫 Acces Interzis</h3>
              <p>Nu aveți permisiunea să accesați această pagină.</p>
              <p>Vă redirectăm către o pagină permisă...</p>
            </div>
          );
        } else {
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h3>🚫 Acces Interzis</h3>
              <p>Nu aveți permisiuni pentru nicio pagină. Contactați administratorul.</p>
            </div>
          );
        }
      }
    }

    // Use V1 components only (green theme)
    switch (page) {
      case 'dashboard':
        const dashboardUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <Dashboard data={safeData} userRole={dashboardUserRole} />;
      case 'fields':
        return <Fields data={safeData} />;
      case 'weather':
        return <Weather />;
      case 'tasks':
        return (
          <Tasks 
            data={safeData} 
            onEdit={handleEditTask} 
            onDelete={handleDeleteTask} 
            onAdd={handleAddTask} 
            onComplete={handleCompleteTask}
          />
        );
      case 'taskForm':
        return (
          <TaskForm 
            task={selectedTask} 
            onSave={handleSaveTask} 
            onCancel={() => {
              setShowTaskForm(false);
              setActiveComponent('tasks');
            }}
            data={safeData}
          />
        );
      case 'inventory':
        const userRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <Inventory data={safeData} userRole={userRole} />;
      case 'worker':
        const currentUser = farmAuth.isAuthenticated ? farmAuth.user : auth.user;
        return <WorkerInterface data={safeData} currentUser={currentUser} />;
      case 'settings':
        const user = farmAuth.isAuthenticated ? farmAuth.user : auth.user;
        return <Settings data={safeData} user={user} />;
      case 'finances':
        return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><div style={{ fontSize: 40 }}>🚧</div><div>Secțiunea Finanțe este în construcție.</div><div style={{ fontSize: 13, marginTop: 8 }}>Folosiți Costuri pentru analiza cheltuielilor.</div></div>;
      case 'parcels':
        const currentUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <ParcelManager userRole={currentUserRole} />;
      case 'seasons':
        return <SeasonManager />;
      case 'stockadjustment':
        const stockUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <StockAdjustment userRole={stockUserRole} />;
      case 'workplanning':
        const workPlanningUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <WorkPlanning userRole={workPlanningUserRole} />;
      case 'arendamanager':
        return <ArendaManager />;
      case 'equipment':
        const equipmentUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <EquipmentManager userRole={equipmentUserRole} />;
      case 'motorina':
        return <Motorina />;
      case 'lucrari':
        return <Lucrari />;
      case 'costuri':
        return <Costuri />;
      default:
        const defaultUserRole = farmAuth.isAuthenticated ? farmAuth.user.role : auth.user.role;
        return <Dashboard data={safeData} userRole={defaultUserRole} />;
    }
  };

  // Determine which navigation items to show based on actual user permissions
  const getNavigationItems = () => {
    if (!farmAuth.isAuthenticated) return [];
    
    const user = farmAuth.user;
    if (!user) return [];

    console.log('getNavigationItems - User:', user.role, 'Permissions:', userPermissions);

    // Managers always have full access
    if (user.role === 'manager') {
      return [
        { id: 'dashboard', label: t('dashboard'), icon: 'dashboard' },
        { id: 'fields', label: t('fields'), icon: 'terrain' },
        { id: 'weather', label: t('weather'), icon: 'cloud' },
        { id: 'tasks', label: t('tasks'), icon: 'assignment' },
        { id: 'inventory', label: t('inventory'), icon: 'inventory' },
        { id: 'equipment', label: t('nav-equipment'), icon: 'precision_manufacturing' },
        { id: 'motorina', label: 'Motorină', icon: 'local_gas_station' },
        { id: 'lucrari', label: 'Lucrări', icon: 'agriculture' },
        { id: 'costuri', label: 'Costuri', icon: 'bar_chart' },
        { id: 'parcels', label: t('parcel-manager'), icon: 'map' },
        { id: 'seasons', label: t('nav-seasons'), icon: 'nature' },
        { id: 'stockadjustment', label: t('nav-stockadjustment'), icon: 'inventory_2' },
        { id: 'workplanning', label: t('nav-workplanning'), icon: 'schedule' },
        { id: 'arendamanager', label: t('nav-arendamanager'), icon: 'home' },
        { id: 'settings', label: t('settings'), icon: 'settings' }
      ];
    }

    // Tank operator: only sees motorina section
    if (user.role === 'tank_operator') {
      return [
        { id: 'motorina', label: 'Motorină', icon: 'local_gas_station' }
      ];
    }

    // If permissions are still loading, show default pages for the role to prevent blank sidebar
    if (!userPermissions) {
      console.log('Permissions not loaded yet, showing default for role:', user.role);
      if (user.role === 'accountant') {
        return [
          { id: 'parcels', label: t('parcel-manager'), icon: 'map' },
          { id: 'arendamanager', label: t('nav-arendamanager'), icon: 'home' },
          { id: 'settings', label: t('settings'), icon: 'settings' }
        ];
      }
      if (user.role === 'worker') {
        return [
          { id: 'fields', label: t('fields'), icon: 'terrain' },
          { id: 'tasks', label: t('tasks'), icon: 'assignment' },
          { id: 'worker', label: t('work-session'), icon: 'work' }
        ];
      }
      return [];
    }

    // For non-managers, check actual permissions
    const allowedPages = userPermissions.pages || [];
    console.log('Allowed pages:', allowedPages);

    // Define all available navigation items with their permission IDs
    const allNavItems = [
      { id: 'dashboard', label: t('dashboard'), icon: 'dashboard', permission: 'dashboard' },
      { id: 'fields', label: t('fields'), icon: 'terrain', permission: 'fields' },
      { id: 'weather', label: t('weather'), icon: 'cloud', permission: 'weather' },
      { id: 'tasks', label: t('tasks'), icon: 'assignment', permission: 'tasks' },
      { id: 'inventory', label: t('inventory'), icon: 'inventory', permission: 'inventory' },
      { id: 'equipment', label: t('nav-equipment'), icon: 'precision_manufacturing', permission: 'equipment' },
      { id: 'motorina', label: 'Motorină', icon: 'local_gas_station', permission: 'motorina' },
      { id: 'lucrari', label: 'Lucrări', icon: 'agriculture', permission: 'lucrari' },
      { id: 'costuri', label: 'Costuri', icon: 'bar_chart', permission: 'costuri' },
      { id: 'worker', label: t('work-session'), icon: 'work', permission: 'worker' },
      { id: 'parcels', label: t('parcel-manager'), icon: 'map', permission: 'parcels' },
      { id: 'seasons', label: t('nav-seasons'), icon: 'nature', permission: 'seasons' },
      { id: 'stockadjustment', label: t('nav-stockadjustment'), icon: 'inventory_2', permission: 'stockadjustment' },
      { id: 'workplanning', label: t('nav-workplanning'), icon: 'schedule', permission: 'workplanning' },
      { id: 'arendamanager', label: t('nav-arendamanager'), icon: 'home', permission: 'arendamanager' },
      { id: 'settings', label: t('settings'), icon: 'settings', permission: 'settings' }
    ];

    // Filter items based on user permissions
    if (allowedPages.includes('all')) {
      // User has access to all pages
      return allNavItems;
    } else {
      // Filter based on specific permissions
      const filteredItems = allNavItems.filter(item => 
        allowedPages.includes(item.permission)
      );
      console.log('Filtered navigation items:', filteredItems);
      return filteredItems;
    }
  };

  const getAvailableNavItems = () => arrangeNavItems(getNavigationItems());

  const resolvePage = () =>
    isPageAvailable(activeComponent) ? activeComponent : (getAvailableNavItems()[0]?.id || activeComponent);

  // Super admin access (for development/testing)
  if (authMode === 'superadmin') {
    return <SuperAdminDashboard onBackToLogin={() => setAuthMode('login')} />;
  }

  // Farm registration flow
  if (authMode === 'register') {
    return (
      <FirebaseFarmRegistration
        onRegistrationSuccess={(result) => { setAuthMode('login'); handleFarmRegistrationSuccess(result); }}
        onBackToLogin={() => setAuthMode('login')}
      />
    );
  }

  // Until the saved session is known, don't flash the login screen.
  if (authLoading && !farmAuth.isAuthenticated && !auth.isAuthenticated) return <LoadingScreen />;

  // Check authentication (prioritize farm auth over legacy auth)
  if (!farmAuth.isAuthenticated && !auth.isAuthenticated) {
    return (
      <div>
        <FirebaseFarmLogin 
          onLoginSuccess={handleFarmLoginSuccess}
          onRegisterRequest={() => setAuthMode('register')}
        />
        
        {/* Super Admin Access - Only for development */}
        {process.env.NODE_ENV === 'development' && (
          <div 
            style={{ 
              position: 'fixed', 
              bottom: '10px', 
              right: '10px', 
              opacity: 0.1 
            }}
          >
            <button 
              onClick={() => setAuthMode('superadmin')}
              style={{ 
                background: 'transparent', 
                border: 'none', 
                fontSize: '10px' 
              }}
            >
              SA
            </button>
          </div>
        )}
      </div>
    );
  }

  const currentUser = farmAuth.isAuthenticated ? farmAuth.user : auth.user;
  const farmName = farmAuth.isAuthenticated ? farmAuth.farm?.name : null;

  // Ensure data has proper defaults
  const safeData = {
    fields: data.fields || [],
    tasks: data.tasks || [],
    weather: data.weather || { current: {}, forecast: [] },
    financialMetrics: data.financialMetrics || { 
      revenue: { total: '€0' }, 
      expenses: { total: '€0' }, 
      profit: { total: '€0' } 
    },
    inventory: data.inventory || { materials: [], equipments: [], invoices: [] },
    workplans: data.workplans || [],
    workers: data.workers || []
  };

  return (
    <AppShell
      user={currentUser}
      farmName={farmName}
      navItems={getAvailableNavItems()}
      activePage={resolvePage()}
      onNavigate={handleNavigation}
      onLogout={handleLogout}
    >
      {renderContent()}
    </AppShell>
  );
}

export default App;